import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Filters, FiltersResponse } from '@models/filters.model';
import { MICROSERVICES } from '@shared/constants/microservices';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';
import { DateFilterDto, FiltersDto } from '../../../domain/dtos/filters.dto';
import { DeletePaymentMethodResponse, PageablePaymentResponse, PaymentMethodResponse, SetupIntentResponse } from '../domain/payments.model';
import { Expense, Payments, SetupIntent } from './interfaces/payments.interface';
import { PaymentsRepository } from './payments.repository';

@Injectable()
export class PaymentsApiService implements PaymentsRepository {
    private _http = inject(HttpClient);

    private readonly months = 6; //Number of months recovery in total-expenses (graph)

    deletePaymentMethod(pmId: string): Observable<DeletePaymentMethodResponse> {
        return this._http.delete<DeletePaymentMethodResponse>(`${environment.url}${MICROSERVICES.payments}/payment_methods/${pmId}`);
    }

    getPaymentFilters(filtersDto: FiltersDto): Observable<Filters> {
        let params = new HttpParams();

        if (filtersDto.vehicleType.length > 0) {
            filtersDto.vehicleType.forEach((type) => {
                params = params.append('vehicleType', type);
            });
        }
        if (filtersDto.licensesPlate.length > 0) {
            filtersDto.licensesPlate.forEach((type) => {
                params = params.append('vehicle', type);
            });
        }
        const options = {
            params,
        };
        return this._http.get<FiltersResponse>(`${environment.url}${MICROSERVICES.payments}/payment-filters`, options).pipe(
            map(({ filters }: FiltersResponse) => {
                const { vehicleTypes, vehicles } = filters;
                return {
                    vehicleTypes,
                    vehicles,
                };
            })
        );
    }

    getPaymentGroup(pmId: string): Observable<Payments> {
        return this._http.get<Payments>(`${environment.url}${MICROSERVICES.payments}/payments/${pmId}`);
    }

    // TODO: Revisar el FiltersDto cuando implementemos los filtros
    // getPaymentGroupList(page = 1, pageSize = 30, filtersDto: FiltersDto): Observable<PageablePaymentResponse> {
    getPaymentGroupList(page = 1, pageSize = 30): Observable<PageablePaymentResponse> {
        const params = new HttpParams().set('page', page).set('pageSize', pageSize);
        // params = this.filtersQueryBuilder(params, filtersDto);
        const options = {
            params,
        };
        return this._http.get<PageablePaymentResponse>(`${environment.url}${MICROSERVICES.payments}/payments`, options);
    }

    getPaymentMethods(): Observable<PaymentMethodResponse> {
        return this._http.get<PaymentMethodResponse>(`${environment.url}${MICROSERVICES.payments}/payment_methods`);
    }

    getTotalExpensesByMonth(): Observable<Expense[]> {
        const params = new HttpParams().set('months', this.months);
        const options = {
            params,
        };

        return this._http
            .get<{ expenses: Expense[] }>(`${environment.url}${MICROSERVICES.payments}/total-expenses`, options)
            .pipe(map((response) => response.expenses.reverse()));
    }

    registerPaymentMethod(): Observable<SetupIntent> {
        const params = new HttpParams().set('paymentMethodType', 'card');

        return this._http
            .post<SetupIntentResponse>(`${environment.url}${MICROSERVICES.payments}/setup_intents`, null, { params })
            .pipe(map((response: SetupIntentResponse) => response.data.tokens));
    }

    setDefaultPaymentMethod(pmId: string): Observable<unknown> {
        const params = new HttpParams().set('pmId', pmId);
        const options = {
            params,
        };

        return this._http.post(`${environment.url}${MICROSERVICES.payments}/payment_methods/${pmId}`, options);
    }

    private filtersQueryBuilder(params: HttpParams, filtersDto: FiltersDto) {
        if (filtersDto.vehicleType.length > 0) {
            filtersDto.vehicleType.forEach((type) => {
                params = params.append('vehicleType', type);
            });
        }
        if (filtersDto.licensesPlate.length > 0) {
            filtersDto.licensesPlate.forEach((type) => {
                params = params.append('vehicle', type);
            });
        }
        if (filtersDto.dateRange.selected) {
            const range: DateFilterDto[] = [
                {
                    from: this.dateTransform(filtersDto.dateRange.range.from),
                    to: this.dateTransform(filtersDto.dateRange.range.to),
                },
            ];
            params = params.set('multipleDateFilter', JSON.stringify(range));
        }
        if (filtersDto.datePeriod.selected) {
            const range: DateFilterDto[] = [];
            filtersDto.datePeriod.years.forEach((year) => {
                filtersDto.datePeriod.months.forEach((month) => {
                    const monthDate = new Date(new Date().getFullYear(), month.numMonth + 1, 0);
                    const numDays = monthDate.getDate();
                    range.push({
                        from: `${year.label}${(month.numMonth + 1).toString().padStart(2, '0')}01`,
                        to: `${year.label}${(month.numMonth + 1).toString().padStart(2, '0')}${numDays}`,
                    });
                });
            });
            params = params.set('multipleDateFilter', JSON.stringify(range));
        }
        return params;
    }

    private dateTransform(value: string): string {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const newDate = `${year}${month}${day}`;
        return newDate;
    }
}
